/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, ArrowLeft, CheckCircle, AlertTriangle } from 'lucide-react';
import PasswordInput, { ConfirmPasswordInput } from '../components/auth/PasswordInput';
import api from '../services/api';

const ChangePassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isExpired = searchParams.get('expired') === 'true';
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordValidation, setPasswordValidation] = useState({ isValid: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Get user info for validation
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Check if form is valid
  const isFormValid = 
    currentPassword.trim() !== '' &&
    passwordValidation.isValid &&
    newPassword === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Get the appropriate token
      const token = isExpired 
        ? localStorage.getItem('tempToken') 
        : localStorage.getItem('token');

      if (!token) {
        setError('Session expired. Please login again.');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      const response = await fetch(`${api.getBaseUrl?.() || 'https://skilldelivery-platform.onrender.com/api'}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Update tokens if returned
        if (data.data?.accessToken) {
          localStorage.setItem('token', data.data.accessToken);
        }
        if (data.data?.refreshToken) {
          localStorage.setItem('refreshToken', data.data.refreshToken);
        }
        
        // Clear temp token if it was used
        localStorage.removeItem('tempToken');
        
        setSuccess(true);
        
        // Redirect after success
        setTimeout(() => {
          if (isExpired) {
            navigate('/login');
          } else {
            navigate(-1); // Go back to previous page
          }
        }, 2000);
      } else {
        setError(data.error || 'Failed to change password');
      }
    } catch (err) {
      console.error('Change password error:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (isExpired) {
      // Clear temp token and go to login
      localStorage.removeItem('tempToken');
      navigate('/login');
    } else {
      navigate(-1);
    }
  };

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Password Changed!</h2>
          <p className="text-gray-600 mb-4">
            Your password has been successfully updated.
          </p>
          <p className="text-sm text-gray-500">
            Redirecting...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Change Password</h2>
            <p className="text-sm text-gray-600">
              {isExpired 
                ? 'Your password has expired. Please create a new one.'
                : 'Update your account password'}
            </p>
          </div>
        </div>

        {/* Expired Warning */}
        {isExpired && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800">Password Expired</p>
              <p className="text-xs text-yellow-700 mt-1">
                For security reasons, passwords expire every 90 days. 
                Please choose a new password that you haven't used before.
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Password
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
              placeholder="Enter your current password"
              required
            />
          </div>

          {/* New Password with Strength Meter */}
          <PasswordInput
            value={newPassword}
            onChange={setNewPassword}
            onValidationChange={setPasswordValidation}
            label="New Password"
            placeholder="Create a new strong password"
            email={user.email}
            showRequirements={true}
            showMeter={true}
          />

          {/* Confirm New Password */}
          <ConfirmPasswordInput
            password={newPassword}
            confirmPassword={confirmPassword}
            onChange={setConfirmPassword}
            label="Confirm New Password"
            placeholder="Confirm your new password"
          />

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || !isFormValid}
            className={`w-full py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${
              isFormValid && !loading
                ? 'bg-cyan-500 text-white hover:bg-cyan-600'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Lock className="w-4 h-4" />
            {loading ? 'Changing Password...' : 'Change Password'}
          </button>
        </form>

        {/* Security Tips */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <p className="text-xs font-medium text-gray-700 mb-2">Security Tips:</p>
          <ul className="text-xs text-gray-600 space-y-1">
            <li>• Use a unique password not used on other sites</li>
            <li>• Don't share your password with anyone</li>
            <li>• Consider using a password manager</li>
            <li>• Password must be at least 12 characters</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
